/** @jsx jsx */
import {
	useCallback,
	useEffect,
	useState,
	type PropsWithChildren,
	useRef,
	type RefCallback,
	useMemo,
} from 'react';
import { jsx } from '@compiled/react';
import Button from '@atlaskit/button';
import { IconButton, type IconButtonProps } from '@atlaskit/button/new';
import Icon from '@atlaskit/icon';
import AutomationIcon from '@atlaskit/icon/core/automation';
import ChevronDownIcon from '@atlaskit/icon/core/migration/chevron-down';
import type { Placement, PopperChildrenProps } from '@atlaskit/popper';
import type { TriggerProps } from '@atlaskit/popup';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { AutomationGlyph } from '@atlassian/jira-automation-menu/src/common/assets/automation.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { CORE_PROJECT, SOFTWARE_PROJECT } from '@atlassian/jira-common-constants/src/index.tsx';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { RecurWorkItemContainer as RecurWorkItemContainerMain } from '@atlassian/jira-recur-work-item/src/controllers/index.tsx';
import type { ContainerProps as RecurWorkItemContainerProps } from '@atlassian/jira-recur-work-item/src/controllers/types.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';
import { POPUP_ID, POPUP_KEYS, type PopupKeys } from './constants.tsx';
import { createActionsMenuDropdownEntrypoint } from './actions-menu-dropdown/entrypoint.tsx';

const tooltipOptions: IconButtonProps['tooltip'] = {
	position: 'top',
	hideTooltipOnClick: true,
};

const customModifiers = [
	{
		name: 'preventOverflow',
		enabled: true,
		options: {
			padding: { top: 65 },
		},
	},
];

const RecurWorkItemContainerEmpty = ({
	children,
}: PropsWithChildren<RecurWorkItemContainerProps>) => children;

const entryPointParams = {};

export const useIsOpen = () => useState(false);

export const ActionsMenu = (props: Props) => {
	const {
		transitions,
		onSelect,
		rules,
		executeRule,
		executingRule,
		triggerFetch,
		initialised,
		issueKey,
		issueId,
		projectType,
		baseAutomationUrl,
		canManageAutomations,
	} = props;

	const [activePopupKey, setActivePopupKey] = useState<PopupKeys | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isOpen, setIsOpen] = useIsOpen();
	const { formatMessage } = useIntl();
	const updatePopupPositionRef = useRef<PopperChildrenProps['update'] | null>(null);

	const canSeeRecurringWorkPopup =
		(projectType === SOFTWARE_PROJECT || projectType === CORE_PROJECT) &&
		expVal('jira-recur-single-work-item-on-schedule', 'showRecurringWorkPopup', false);

	const canSeeRecurringWorkPopupWithNewTrigger =
		canSeeRecurringWorkPopup && fg('jira-recur-single-work-item-on-sched-due-date-gate');

	const onTriggerClick = useCallback(() => {
		if (!isOpen) {
			const analyticsEvent = createAnalyticsEvent({
				actionSubject: 'menu',
				action: 'opened',
			});
			fireUIAnalytics(analyticsEvent, 'manualRulesActionsMenu', {
				userType: canManageAutomations ? 'admin' : 'endUser',
			});
		}

		if (isOpen) setActivePopupKey(null);
		setIsOpen(!isOpen);
	}, [canManageAutomations, createAnalyticsEvent, isOpen, setIsOpen]);

	// fallback placements added as per atlaskit dropdown-menu
	const fallbackPlacements: Placement[] = [
		'bottom',
		'bottom-end',
		'top-start',
		'top',
		'top-end',
		'auto',
	];

	const onClose = useCallback(() => {
		setIsOpen(false);
		setActivePopupKey(null);
	}, [setIsOpen, setActivePopupKey]);

	const RecurWorkItemContainer =
		canSeeRecurringWorkPopup || canSeeRecurringWorkPopupWithNewTrigger
			? RecurWorkItemContainerMain
			: RecurWorkItemContainerEmpty;

	useEffect(() => {
		fg('jira-recur-single-work-item-on-sched-due-date-gate') && updatePopupPositionRef.current?.();

		if (activePopupKey === POPUP_KEYS.recurWork) {
			globalThis.document.querySelector<HTMLDivElement>(`#${POPUP_ID}`)?.focus();
		}
	}, [activePopupKey]);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		createActionsMenuDropdownEntrypoint,
		entryPointParams,
	);
	const triggerRef = useEntryPointButtonTrigger(entryPointActions, !isOpen);

	const runtimeProps = useMemo(
		() => ({
			transitions,
			onSelect,
			rules,
			executeRule,
			executingRule,
			triggerFetch,
			initialised,
			issueKey,
			issueId,
			projectType,
			baseAutomationUrl,
			canManageAutomations,
			activePopupKey,
			setActivePopupKey,
			canSeeRecurringWorkPopup,
			canSeeRecurringWorkPopupWithNewTrigger,
			onClose,
		}),
		[
			transitions,
			onSelect,
			rules,
			executeRule,
			executingRule,
			triggerFetch,
			initialised,
			issueKey,
			issueId,
			projectType,
			baseAutomationUrl,
			canManageAutomations,
			activePopupKey,
			setActivePopupKey,
			canSeeRecurringWorkPopup,
			canSeeRecurringWorkPopupWithNewTrigger,
			onClose,
		],
	);

	return (
		<RecurWorkItemContainer issueKey={issueKey}>
			<Popup
				messageId="issue-field-status.ui.status-view.actions.actions-menu.popup"
				messageType="transactional"
				isOpen={isOpen}
				placement="bottom-start"
				fallbackPlacements={fallbackPlacements}
				id={POPUP_ID}
				trigger={(triggerProps: TriggerProps) => {
					const trigger = isVisualRefreshEnabled() ? (
						<IconButton
							{...triggerProps}
							isSelected={isOpen}
							onClick={onTriggerClick}
							icon={AutomationIcon}
							isTooltipDisabled={false}
							tooltip={tooltipOptions}
							label={formatMessage(messages.automation)}
							interactionName="jira-issue-field-status.ui.status-view.actions.actions-menu.button"
							ref={(element: HTMLElement | null) => {
								triggerRef(element);
								// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								(triggerProps.ref as RefCallback<HTMLElement>)(element);
							}}
						/>
					) : (
						<Button
							{...triggerProps}
							appearance="subtle"
							isSelected={isOpen}
							onClick={onTriggerClick}
							iconAfter={<ChevronDownIcon label="" size="small" />}
							iconBefore={
								<Icon size="medium" glyph={AutomationGlyph} label="" secondaryColor="none" />
							}
							interactionName="jira-issue-field-status.ui.status-view.actions.actions-menu.button"
							ref={(element) => {
								triggerRef(element);
								// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								(triggerProps.ref as RefCallback<HTMLElement>)(element);
							}}
						>
							{formatMessage(messages.actions)}
						</Button>
					);

					return trigger;
				}}
				onClose={onClose}
				content={({ update }) => {
					if (fg('jira-recur-single-work-item-on-sched-due-date-gate')) {
						updatePopupPositionRef.current = update;
					}
					return (
						<JiraEntryPointContainer
							entryPointReferenceSubject={entryPointReferenceSubject}
							id="actions-menu-dropdown-entrypoint"
							packageName="jira-actions-menu-dropdown"
							fallback={null}
							runtimeProps={runtimeProps}
						/>
					);
				}}
				zIndex={layers.modal}
				shouldRenderToParent
				modifiers={customModifiers}
				testId="issue-field-status.ui.status-view.actions.actions-menu.popup"
			/>
		</RecurWorkItemContainer>
	);
};
